import React, { useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
} from 'react-bootstrap';
import {
  createZabbixDataSource,
  deleteZabbixDataSource,
  getZabbixDataSources,
  testZabbixDataSource,
  updateZabbixDataSource,
  ZabbixDataSource,
  ZabbixDataSourcePayload,
} from './zabbixDataSourceService';
import './UserPanel.css';

const emptyForm: ZabbixDataSourcePayload = {
  name: '',
  baseUrl: '',
  username: '',
  password: '',
  isPlatform: false,
  status: 'active',
};

const ZabbixDataSourcePage: React.FC = () => {
  const [items, setItems] = useState<ZabbixDataSource[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<ZabbixDataSource | null>(null);
  const [form, setForm] = useState<ZabbixDataSourcePayload>(emptyForm);

  const loadData = async () => {
    setLoading(true);
    setError('');

    try {
      const data = await getZabbixDataSources();
      setItems(data || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load Zabbix data sources.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openCreateModal = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const openEditModal = (item: ZabbixDataSource) => {
    setEditingItem(item);
    setForm({
      name: item.name,
      baseUrl: item.baseUrl,
      username: item.username,
      password: '',
      isPlatform: item.isPlatform,
      status: item.status || 'active',
    });
    setError('');
    setSuccess('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingItem(null);
    setForm(emptyForm);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const target = e.target as HTMLInputElement;
    const { name, value, type, checked } = target;

    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const validateForm = (): string | null => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.baseUrl.trim()) return 'Base URL is required.';
    if (!form.username.trim()) return 'Username is required.';

    if (!editingItem && !form.password.trim()) {
      return 'Password is required.';
    }

    if (!form.baseUrl.includes('/api_jsonrpc.php')) {
      return 'Base URL should usually end with /api_jsonrpc.php.';
    }

    return null;
  };

  const handleSave = async () => {
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (editingItem) {
        await updateZabbixDataSource(editingItem.id, form);
        setSuccess('Zabbix data source updated successfully.');
      } else {
        await createZabbixDataSource(form);
        setSuccess('Zabbix data source created successfully.');
      }

      closeModal();
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save Zabbix data source.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item: ZabbixDataSource) => {
    const confirmed = window.confirm(`Delete Zabbix data source "${item.name}"?`);
    if (!confirmed) return;

    setDeletingId(item.id);
    setError('');
    setSuccess('');

    try {
      await deleteZabbixDataSource(item.id);
      setSuccess('Zabbix data source deleted successfully.');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete Zabbix data source.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleTest = async (item: ZabbixDataSource) => {
    setTestingId(item.id);
    setError('');
    setSuccess('');

    try {
      await testZabbixDataSource(item.id);
      setSuccess(`Connection test succeeded: ${item.name}`);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Connection test failed.');
    } finally {
      setTestingId(null);
    }
  };

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <Row className="align-items-center mb-3">
          <Col>
            <h4 className="mb-1">Zabbix Data Sources</h4>
            <div className="text-muted">
              Manage Zabbix API connections for monitoring data integration.
            </div>
          </Col>
          <Col xs="auto">
            <Button variant="primary" onClick={openCreateModal}>
              Add Data Source
            </Button>
          </Col>
        </Row>

        {error && (
          <Alert variant="danger" onClose={() => setError('')} dismissible>
            {error}
          </Alert>
        )}

        {success && (
          <Alert variant="success" onClose={() => setSuccess('')} dismissible>
            {success}
          </Alert>
        )}

        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" />
          </div>
        ) : (
          // <Table bordered hover responsive className="align-middle">
             <Table striped bordered hover size="sm" responsive variant="dark">
            <thead>
              <tr>
                <th>ID</th>
                <th>Name</th>
                <th>Base URL</th>
                <th>Username</th>
                <th>Scope</th>
                <th>Status</th>
                <th>Token</th>
                <th style={{ width: 280 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">
                    No Zabbix data sources found.
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id}>
                    <td>{item.id}</td>
                    <td>{item.name}</td>
                    <td className="text-break">{item.baseUrl}</td>
                    <td>{item.username}</td>
                    <td>
                      {item.isPlatform ? (
                        <Badge bg="dark">Platform</Badge>
                      ) : (
                        <Badge bg="secondary">Tenant</Badge>
                      )}
                    </td>
                    <td>
                      {item.status === 'active' ? (
                        <Badge bg="success">Active</Badge>
                      ) : (
                        <Badge bg="warning" text="dark">
                          Disabled
                        </Badge>
                      )}
                    </td>
                    <td>
                      {item.hasToken ? (
                        <Badge bg="success">Available</Badge>
                      ) : (
                        <Badge bg="secondary">Empty</Badge>
                      )}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="outline-success"
                        className="me-2"
                        disabled={testingId === item.id}
                        onClick={() => handleTest(item)}
                      >
                        {testingId === item.id ? 'Testing...' : 'Test'}
                      </Button>

                      <Button
                        size="sm"
                        variant="outline-primary"
                        className="me-2"
                        onClick={() => openEditModal(item)}
                      >
                        Edit
                      </Button>

                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deletingId === item.id}
                        onClick={() => handleDelete(item)}
                      >
                        {deletingId === item.id ? 'Deleting...' : 'Delete'}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Card.Body>

      <Modal show={showModal} onHide={closeModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingItem ? 'Edit Zabbix Data Source' : 'Add Zabbix Data Source'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Name</Form.Label>
                  <Form.Control
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Zabbix-Prod"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Status</Form.Label>
                  <Form.Select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                  >
                    <option value="active">Active</option>
                    <option value="disabled">Disabled</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Base URL</Form.Label>
              <Form.Control
                name="baseUrl"
                value={form.baseUrl}
                onChange={handleChange}
                placeholder="http://8.219.228.240:8080/api_jsonrpc.php"
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Username</Form.Label>
                  <Form.Control
                    name="username"
                    value={form.username}
                    onChange={handleChange}
                    placeholder="Admin"
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>Password</Form.Label>
                  <Form.Control
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder={editingItem ? 'Enter new password' : 'Password'}
                  />
                  {editingItem && (
                    <Form.Text className="text-muted">
                      Leave empty only if your backend supports keeping the old password.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-2">
              <Form.Check
                type="checkbox"
                name="isPlatform"
                label="Platform-level data source"
                checked={form.isPlatform}
                onChange={handleChange}
              />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>
            Cancel
          </Button>
          <Button variant="primary" disabled={saving} onClick={handleSave}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Card>
  );
};

export default ZabbixDataSourcePage;